import { Component, type ReactNode, type ErrorInfo } from 'react';
import { captureSentry } from '../lib/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    // 3.9s — also ship to Sentry with the React component stack as
    // extra context. With source maps uploaded at build time, the
    // minified 'r.bg' style traces resolve back to real symbols.
    captureSentry(error, { componentStack: info.componentStack });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px 24px',
            textAlign: 'center',
            minHeight: '200px',
          }}
        >
          <div style={{ fontSize: 'var(--fs-xl, 22px)', fontWeight: 700, color: 'var(--text-primary, #1a1a1a)', marginBottom: '8px' }}>
            Algo salió mal
          </div>
          <div style={{ fontSize: 'var(--fs-sm, 14px)', color: 'var(--text-secondary, #666)', marginBottom: '16px', maxWidth: '360px' }}>
            {this.state.error?.message || 'Error inesperado'}
          </div>
          <button
            onClick={() => {
              this.setState({ hasError: false, error: null });
              window.location.reload();
            }}
            style={{
              height: '44px',
              padding: '0 24px',
              borderRadius: 'var(--radius-md, 8px)',
              border: 'none',
              background: 'var(--status-info, #1a6bb5)',
              color: 'white',
              fontSize: 'var(--fs-base, 16px)',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Recargar
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
